import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from '@mysimpledb/shared/config/axios';
import '../styles/global.css';
import './ResetPassword.css';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = useMemo(
    () => String(searchParams.get('token') || '').trim(),
    [searchParams]
  );

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [complete, setComplete] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    setStatusType('');

    if (!token) {
      setStatusType('error');
      setStatus('This password reset link is missing its security token.');
      return;
    }

    if (password.length < 8) {
      setStatusType('error');
      setStatus('Your new password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setStatusType('error');
      setStatus('The two passwords do not match.');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post('/password/reset-password', { token, password });

      setComplete(true);
      setPassword('');
      setConfirmPassword('');
      setStatusType('success');
      setStatus(
        'Your password has been changed. You can now sign in with your new password.'
      );
    } catch (err) {
      console.error('Password reset failed:', err);
      setStatusType('error');
      setStatus(
        err.response?.data?.error ||
          'We could not reset your password. The link may be invalid or expired.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="reset-page">
      <section
        className="reset-container"
        aria-labelledby="reset-password-title"
      >
        <h1 id="reset-password-title">Choose a new password</h1>

        <p className="reset-intro">
          Enter a new password for your account. It must be at least 8
          characters long.
        </p>

        {!token ? (
          <div className="reset-status error" role="alert">
            This password reset link is invalid or incomplete. Please request a
            new reset link.
          </div>
        ) : complete ? (
          <>
            <div className="reset-status success" role="status">
              {status}
            </div>
            <p className="reset-back">
              <Link to="/login">Continue to Sign In</Link>
            </p>
          </>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="reset-form">
              <label htmlFor="new-password">New password</label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                minLength={8}
                required
              />

              <label htmlFor="confirm-password">Confirm new password</label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={submitting}
                minLength={8}
                required
              />

              <button
                className="standard-button reset-submit"
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'Updating…' : 'Reset Password'}
              </button>
            </form>

            {status ? (
              <div
                className={`reset-status ${statusType}`}
                role={statusType === 'error' ? 'alert' : 'status'}
              >
                {status}
              </div>
            ) : null}

            <p className="reset-back">
              <Link to="/login">Back to Sign In</Link>
            </p>
          </>
        )}
      </section>
    </main>
  );
}

export default ResetPassword;
